import type {DropCommitContext, FragmentPointer, Namespace, Pointer} from '@wix/document-services-types'
import {createImmutableProxy} from '@wix/wix-immutable-proxy'
import type {EventEmitter} from 'events'
import _ from 'lodash'
import type {CoreConfig} from '../../documentManagerCore'
import type {
    BaselineBlacklistCheck,
    DocumentDataTypes,
    FilterGetter,
    IndexKey,
    KeyValPredicate,
    Null,
    NullUndefined,
    PostSetOperation,
    PostTransactionOperation,
    Transaction,
    ValidationWhitelistCheck
} from '../../types'
import type {IndexedValues, ValueToIndexIds} from '../queryIndex/queryIndex'
import {createDalSchema} from '../schema/dalSchema'
import {createSnapshotChain, SnapshotDal} from '../snapshot/snapshotDal'
import {createList} from '../snapshot/snapshotList'
import {createTagManager} from '../snapshot/tagManager'
import {createStore, DalJsStore, DmStore} from '../store'
import {FragmentStore, createFragmentStore, type FragmentStoreMap, FragmentDalJsStore} from '../fragmentStore'
import {createValidator, ValidateValue} from '../validation/dalValidation'
import {CreateFragmentArgs, createFragmentDal, FragmentDal} from './fragmentDal'
import {
    basedOnSignatureNS,
    CommitsDisabledError,
    CustomGetter,
    DalValueChangeCallback,
    TransactionEvent,
    TransactionEvents
} from '../documentManagerDal'
import {BaseDal, getPointersByNamespaceKeys, DAL, CreateArgs} from '../baseDal'
import {isFragmentPointer} from '../../utils/pointerUtils'

export const PAGE_FRAGMENT_TYPE = 'Pages'

class Registrar {
    pagesDal: FragmentDal
    constructor(private fragmentDalManager: FragmentDalManager, private fragmentDALs: FragmentDALs) {
        this.pagesDal = this.fragmentDALs[PAGE_FRAGMENT_TYPE]
    }

    registerFilter(name: string, filter: ValueToIndexIds): void {
        Object.values(_.omit(this.fragmentDALs, PAGE_FRAGMENT_TYPE)).forEach(fragmentDal => {
            fragmentDal._queryIndex.createFilterFactory(name, filter)
        })
        const filterFactory = this.pagesDal._queryIndex.createFilterFactory(name, filter)
        this.fragmentDalManager.queryFilterGetters[filterFactory.indexName] = filterFactory.getFilter
    }

    registerValidator(name: string, validateValue: ValidateValue): void {
        this.pagesDal.validator.registerValidator(name, validateValue)
    }

    registerRebaseValidator(name: string, validateValue: ValidateValue): void {
        this.pagesDal.rebaseValidator.registerValidator(name, validateValue)
    }

    registerValidationWhitelistCheck(whitelistCheck: ValidationWhitelistCheck) {
        this.pagesDal.validator.registerWhitelistCheck(whitelistCheck)
    }

    registerPostTransactionOperation(name: string, operation: PostTransactionOperation): void {
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            fragmentDal.postTransactionOperations[name] = operation
        })
    }

    registerTransactionDerivedMetadata(name: string, operation: PostTransactionOperation, fragmentType?: string): void {
        if (fragmentType) {
            const fragmentDal = this.fragmentDALs[fragmentType]
            if (!fragmentDal) {
                throw new Error(`Fragment type ${fragmentType} is not supported`)
            }
            fragmentDal.transactionDerivedMetadataOperations[name] = operation
        } else {
            Object.values(this.fragmentDALs).forEach(fragmentDal => {
                fragmentDal.transactionDerivedMetadataOperations[name] = operation
            })
        }
    }

    registerBaselineBlacklistCheck(check: BaselineBlacklistCheck): void {
        this.pagesDal.validator.registerBaselineBlacklistCheck(check)
    }

    registerForChangesCallback(callback: DalValueChangeCallback) {
        this.pagesDal.updateCallbacks.push(callback)
    }

    unregisterForChangesCallback(callback: DalValueChangeCallback) {
        _.remove(this.pagesDal.updateCallbacks, e => e === callback)
    }
}

interface FragmentDALs {
    [key: string]: FragmentDal
}

class FragmentDalManager extends BaseDal implements DAL {
    queryFilterGetters: Record<string, FilterGetter> = {}
    tagManager = createTagManager()
    registrar: Registrar
    readonly schema
    rebaseValidator

    private fragmentDALs: FragmentDALs = {}
    private basedOnSignatureMap: Record<string, string> = {}

    constructor(
        protected coreConfig: CoreConfig,
        protected postSetOperations: PostSetOperation[],
        protected dmTypes: DocumentDataTypes,
        protected customGetters: Record<string, CustomGetter>,
        protected eventEmitter: EventEmitter,
        initialStore?: FragmentStoreMap
    ) {
        super(createList(), null, true)
        this.commitsEnabled = true
        this.schema = createDalSchema({
            schemaService: this.coreConfig.schemaService,
            experiments: {}
        })
        this.rebaseValidator = createValidator(this.coreConfig, [basedOnSignatureNS])

        const fragmentTypes = coreConfig.supportedFragments
            ? _.keys(coreConfig.supportedFragments).concat(PAGE_FRAGMENT_TYPE)
            : [PAGE_FRAGMENT_TYPE]
        fragmentTypes.forEach(fragmentType => {
            this.fragmentDALs[fragmentType] = createFragmentDal(this.getFragmentArgs(fragmentType))
        })
        this.registrar = new Registrar(this, this.fragmentDALs)

        if (initialStore) {
            this.mergeToApprovedStore(createFragmentStore(initialStore))
        }
    }

    private getFragmentArgs(fragmentType: string): CreateFragmentArgs {
        return {
            fragmentType,
            coreConfig: this.coreConfig,
            queryFilterGetters: this.queryFilterGetters,
            postSetOperations: this.postSetOperations,
            dmTypes: this.dmTypes,
            customGetters: this.customGetters,
            eventEmitter: this.eventEmitter,
            lastApproved: this.lastApproved,
            snapshots: this.snapshots,
            tagManager: this.tagManager,
            customGettersEnabled: this.customGettersEnabled
        }
    }

    private updateLastApprovedSnapshot(snapshot: SnapshotDal) {
        this.lastApproved = snapshot
        this.applyToAllDALs(dal => dal.updateLastApprovedSnapshot(snapshot))
    }

    private applyToAllDALs<T>(operation: (dal: FragmentDal) => any): Map<string, T> {
        const resultMap = new Map<string, any>()

        _(this.fragmentDALs)
            .toPairs()
            .forEach(([fragmentType, fragmentDal]) => {
                const fragmentResult = operation(fragmentDal)
                if (!_.isNil(fragmentResult)) {
                    resultMap.set(fragmentType, fragmentResult)
                }
            })

        return resultMap
    }

    private applyFragmentChangesOnDALs<T>(
        fragmentStore: FragmentStore,
        operation: (dal: FragmentDal, store: DmStore) => any
    ): Map<string, T> {
        const resultMap = new Map<string, any>()

        fragmentStore.forEach((store, fragmentType) => {
            const dal = fragmentType === PAGE_FRAGMENT_TYPE ? this.pagesDal : this.getFragmentDal(fragmentType)
            resultMap.set(fragmentType, operation(dal, store))
        })

        return resultMap
    }

    private validateFragmentInstance(pointer: Pointer, value: any) {
        const {fragmentInstance} = pointer
        const metaDataFragmentInstance = value?.metaData?.fragmentInstance
        if (fragmentInstance && metaDataFragmentInstance && fragmentInstance !== metaDataFragmentInstance) {
            throw new Error(
                `Fragment instance ${pointer.fragmentInstance} sent in pointer doesn't match fragment instance in metaData ${value.metaData.fragmentInstance}`
            )
        }
    }

    getMergedStoreAsJson(includeCurrentTransaction: boolean): DalJsStore {
        const mergedStore = this.pagesDal.getMergedStore(includeCurrentTransaction) as DmStore
        return mergedStore.asJson()
    }

    enableShouldNotifyAboutReads(): void {
        this.shouldNotifyAboutReads = true
        this.applyToAllDALs(dal => dal.enableShouldNotifyAboutReads())
    }
    /**
     * Merge all the changes since the last approved snapshot up to the new approved
     * snapshot into the main store and update the approved snapshot pointer.
     * Rebuild the tentative store with the remaining unapproved changes.
     * @param id id of the approved snapshot
     */
    approve(id: string) {
        const tx = this.snapshots.findByIdLimit(id, this.lastApproved!)
        if (!tx) {
            throw this.createCannotApproveError(id)
        }

        tx.getAllFragmentsStore().forEach((_store, fragmentType) => {
            if (fragmentType === PAGE_FRAGMENT_TYPE) {
                this.pagesDal.approve(tx)
            } else {
                this.getFragmentDal(fragmentType).approve(tx)
            }
        })

        this.updateLastApprovedSnapshot(tx)
    }

    /**
     * Delete all the tentative snapshots from the snapshot with the given id to the last
     * approved snapshot and rebuild the tentative store with the remaining tentative snapshots
     * @param id - id of the rejected snapshot
     */
    reject(id: string) {
        const tx = this.snapshots.findByIdLimit(id, this.lastApproved!)
        if (!tx) {
            // Possibly sent and later identified as conflict, not necessarily a bug
            return
        }

        this.applyOnSnapshotsRange(this.lastApproved!, tx, (snapshot: SnapshotDal) => {
            this.snapshots.remove(snapshot)
            this.tagManager.remove(snapshot)
        })

        this.applyToAllDALs(dal => dal.removeConflictsAndReport(TransactionEvents.TRANSACTION_REJECTED))
    }

    /**
     * Add the current open transaction to the snapshot chain and merge it to the tentative store.
     * Notify listeners of the commit
     * @param committer
     * @param skipValidations
     */
    commitTransaction(committer?: string | undefined, skipValidations?: boolean | undefined): SnapshotDal {
        if (!this.commitsEnabled) {
            this.coreConfig.logger.captureError(new CommitsDisabledError())
            return this.snapshots.last()!
        }

        const allDALsHaveNothingToCommit = _.every(
            Array.from(this.applyToAllDALs<boolean>(dal => dal.hasNothingToCommit()).values())
        )
        if (allDALsHaveNothingToCommit) {
            return this.snapshots.last()!
        }

        const changes: FragmentStoreMap = this.applyToAllDALs<DmStore>(dal =>
            dal.commitTransactionForFragment(committer, skipValidations)
        )
        if (!changes.size) {
            return this.snapshots.last()!
        }

        const changesStore = createFragmentStore(changes)
        const snapshot = this.snapshots.add(changesStore)

        this.applyFragmentChangesOnDALs<void>(changesStore, (dal, store) =>
            dal.report(store, TransactionEvents.LOCAL_TRANSACTION_APPROVED)
        )

        return snapshot
    }

    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate, fragmentType: string = PAGE_FRAGMENT_TYPE) {
        return this.getFragmentDal(fragmentType).find(namespace, indexKey, predicate)
    }

    getIndexed(indexKey: IndexKey, fragmentType: string = PAGE_FRAGMENT_TYPE): IndexedValues {
        return this.getFragmentDal(fragmentType).getIndexed(indexKey)
    }

    getIndexKeys(indexName: string, fragmentType: string = PAGE_FRAGMENT_TYPE): string[] {
        return this.getFragmentDal(fragmentType).getIndexKeys(indexName)
    }

    private getFragmentDal(fragmentType: string): FragmentDal {
        const fragmentDal = this.fragmentDALs[fragmentType]
        if (!fragmentDal) {
            throw new Error(`Fragment type ${fragmentType} is not supported`)
        }
        return fragmentDal
    }

    get(pointer: Pointer) {
        if (isFragmentPointer(pointer)) {
            const value = this.getFragmentDal(pointer.fragmentType).get(pointer)
            this.validateFragmentInstance(pointer, value)
            return value
        }
        return this.pagesDal.get(pointer)
    }

    has(pointer: Pointer): boolean {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).has(pointer)
        }
        return this.pagesDal.has(pointer)
    }

    isDirty(pointer: Pointer): boolean {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).isDirty(pointer)
        }
        return this.pagesDal.isDirty(pointer)
    }

    query(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate | undefined,
        fragmentType: string = PAGE_FRAGMENT_TYPE
    ): Record<string, any> {
        return this.getFragmentDal(fragmentType).query(namespace, indexKey, optionalPredicate)
    }

    queryKeys(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate | undefined,
        fragmentType: string = PAGE_FRAGMENT_TYPE
    ) {
        return this.getFragmentDal(fragmentType).queryKeys(namespace, indexKey, optionalPredicate)
    }

    /**
     * Returns all the pointers in the index referred to by the specified index key and optionally only in the given namespace
     * @param indexKey
     * @param namespace
     * @param fragmentType
     * @returns An array of the pointers in the specified index
     */
    getIndexPointers(
        indexKey: IndexKey,
        namespace?: string | undefined,
        fragmentType: string = PAGE_FRAGMENT_TYPE
    ): Pointer[] {
        return this.getFragmentDal(fragmentType).getIndexPointers(indexKey, namespace, fragmentType)
    }

    /**
     * Insert the given snapshot into the snapshot chain at the specified position
     *
     * @param change
     * @param position
     * @param event
     * @param id
     */
    _rebase(change: FragmentStore, position: string, event: TransactionEvent, id: string) {
        const tx = this.snapshots.findByIdLimitInclusive(position, this.lastApproved!)
        if (!tx) {
            return this.rebaseApprovedHistory(change, position, event, id)
        }
        const newSnapshot = this.snapshots.insert(change, position, id)
        this.applyFragmentChangesOnDALs(change, (dal, store) =>
            dal.removeConflictsReportAndVerify(store, position, event, newSnapshot.id)
        )
        return newSnapshot
    }

    remove(pointer: Pointer): void {
        if (isFragmentPointer(pointer)) {
            this.getFragmentDal(pointer.fragmentType).remove(pointer)
        } else {
            this.pagesDal.remove(pointer)
        }
    }

    set(pointer: Pointer, value: any): void {
        if (isFragmentPointer(pointer)) {
            this.validateFragmentInstance(pointer, value)
            this.getFragmentDal(pointer.fragmentType).set(pointer, value)
            this.addBasedOnSignatureToMap(pointer)
        } else {
            this.pagesDal.set(pointer, value)
        }
    }

    addBasedOnSignatureToMap(pointer: Pointer): void {
        const {id, type, fragmentType} = pointer
        if (!fragmentType || !this.hasSignature(pointer)) {
            return
        }
        this.basedOnSignatureMap[this.getBasedOnSignatureId(type, id)] = fragmentType
    }

    setIfChanged(pointer: Pointer, value: any): void {
        if (isFragmentPointer(pointer)) {
            this.getFragmentDal(pointer.fragmentType).setIfChanged(pointer, value)
        } else {
            this.pagesDal.setIfChanged(pointer, value)
        }
    }

    touch(pointer: Pointer): void {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).touch(pointer)
        }
        return this.pagesDal.touch(pointer)
    }

    validate(tags?: Record<string, any>, fragmentType?: string) {
        if (fragmentType) {
            const dal = this.fragmentDALs[fragmentType]
            dal.validate(tags)
            return
        }
        this.applyToAllDALs(dal => dal.validate(tags))
    }

    validatePendingCommit(tags?: Record<string, any> | undefined): void {
        this.applyToAllDALs(dal => dal.validatePendingCommit(tags))
    }

    createValidationBaseline() {
        this.pagesDal.createValidationBaseline()
    }

    recreateValidationBaseline() {
        this.applyToAllDALs(dal => dal.recreateValidationBaseline())
    }

    sign(
        rootPointer: Pointer,
        value: any,
        signOver?: Null<SnapshotDal> | undefined
    ): {value: any; basedOnSignature: NullUndefined<string>} {
        if (isFragmentPointer(rootPointer)) {
            return this.getFragmentDal(rootPointer.fragmentType).sign(rootPointer, value, signOver)
        }
        return this.pagesDal.sign(rootPointer, value, signOver)
    }

    setBasedOnSignature(pointer: Pointer, basedOnSignature: NullUndefined<string>): void {
        if (isFragmentPointer(pointer)) {
            this.getFragmentDal(pointer.fragmentType).setBasedOnSignature(pointer, basedOnSignature)
        } else {
            this.pagesDal.setBasedOnSignature(pointer, basedOnSignature)
        }
    }

    getTentativeAndAcceptedAsTransaction(): Transaction {
        const tentativeAndAccepted = this.pagesDal.getMergedStore(false) as DmStore
        const transaction = {
            id: 'tentativeAndAccepted',
            items: tentativeAndAccepted.getValues()
        }
        return createImmutableProxy(transaction)
    }

    getCurrentOpenTransaction() {
        return this.pagesDal.getCurrentOpenTransaction()
    }

    /**
     * Copies changes into the dal store. Called during DS initialization
     * @param changes - the store to copy into the dal store
     * @param label
     */
    mergeToApprovedStore(changes: FragmentStore, label?: string): SnapshotDal {
        const snapshotId = this.snapshots.generateId(label)
        const fragmentStore = changes.clone()
        this.applyFragmentChangesOnDALs(fragmentStore, (dal, store) =>
            dal.validateAndMergeToApprovedStore(store, snapshotId)
        )

        const newApprovedSnapshot = this.lastApproved
            ? this.snapshots.insert(fragmentStore, this.lastApproved.id, snapshotId)
            : this.snapshots.add(fragmentStore, snapshotId)

        this.updateLastApprovedSnapshot(newApprovedSnapshot)
        return newApprovedSnapshot
    }

    dropUncommittedTransaction(reason?: string, e?: any, context: DropCommitContext = {}) {
        this.applyToAllDALs(dal => dal.dropUncommittedTransaction(reason, e, context))
    }

    getAllItemsInNamespace(namespace: Namespace): Pointer[] {
        const allFragmentNamespaceObj = this.applyToAllDALs<Record<string, any>>(dal =>
            dal.getMergedNamespace(namespace)
        )
        const mergedNamespaceObj = Object.assign({}, ...allFragmentNamespaceObj.values())
        return getPointersByNamespaceKeys(Object.keys(mergedNamespaceObj), namespace)
    }

    getItemsInNamespace(fragmentType: string, namespace: Namespace): FragmentPointer[] {
        return this.getFragmentDal(fragmentType).getAllItemsInNamespace(namespace) as FragmentPointer[]
    }

    getNamespacesInFragment(fragmentType: string): Namespace[] {
        return this.getFragmentDal(fragmentType).getNamespaces()
    }

    private checkForConflictingChanges(changes: FragmentStore): boolean {
        const conflictResults = this.applyFragmentChangesOnDALs<boolean>(changes, (dal, store) =>
            dal.isConflictingWithApprovedValue(store)
        )

        return _.some(Array.from(conflictResults.values()))
    }

    private rebaseApprovedHistory(change: FragmentStore, position: string, event: TransactionEvent, id?: string) {
        const tx = this.snapshots.findById(position)
        if (!tx) {
            throw this.createRebaseUnlinkedError(position, id)
        }

        if (this.checkForConflictingChanges(change)) {
            throw this.createRemovedApprovedError(position, id)
        }

        this.coreConfig.logger.captureError(this.createCannotRebaseError(position, id))

        const approvedLater: FragmentStore = _.reduce(
            createSnapshotChain(tx, this.lastApproved, 'rebaseApprovedHistory'),
            (mergedStore: FragmentStore, snapshotInRange: SnapshotDal) => {
                snapshotInRange.getAllFragmentsStore().forEach((store, fragmentType) => {
                    if (!mergedStore.hasFragmentStore(fragmentType)) {
                        mergedStore.setFragmentStore(fragmentType, createStore())
                    }
                    mergedStore.getFragmentStore(fragmentType)!.merge(store)
                })
                return mergedStore
            },
            createFragmentStore()
        )

        const relevantChanges: FragmentStore = createFragmentStore()
        change.forEach((changes: DmStore, fragmentType: string) => {
            changes.forEach((pointer: any, value: any) => {
                if (!approvedLater.getFragmentStore(fragmentType)?.has(pointer)) {
                    if (!relevantChanges.hasFragmentStore(fragmentType)) {
                        relevantChanges.setFragmentStore(fragmentType, createStore())
                    }
                    relevantChanges.getFragmentStore(fragmentType)!.set(pointer, value)
                }
            })
        })

        this.applyFragmentChangesOnDALs(relevantChanges, (dal, store) => dal.mergeToApprovedStore(store))

        const newSnapshot = this.snapshots.insert(relevantChanges, position, id)
        this.applyFragmentChangesOnDALs(relevantChanges, (dal, store) =>
            dal.removeConflictsReportAndVerify(store, position, event, newSnapshot.id)
        )
        return newSnapshot
    }

    getMergedStore(includeCurrentTransaction: boolean): FragmentStore {
        return createFragmentStore(this.applyToAllDALs<DmStore>(dal => dal.getMergedStore(includeCurrentTransaction)))
    }

    _getFragmentsMergedStoreAsJson(): FragmentDalJsStore {
        const mergedStore = this.getMergedStore(false)
        return mergedStore.asJson()
    }

    get _store() {
        return this.pagesDal._store
    }

    get _tentativeStore() {
        return this.pagesDal._tentativeStore
    }

    _getApprovedStoreAsJson() {
        return this.pagesDal._getApprovedStoreAsJson()
    }

    _getTentativeStoreAsJson() {
        return this.pagesDal._getTentativeStoreAsJson()
    }

    /** @private */
    get _queryIndex() {
        return this.pagesDal._queryIndex
    }

    /** @private */
    get _currentTransaction() {
        return this.pagesDal._currentTransaction
    }

    _getBaseLineViolations() {
        return this.pagesDal.validator.getBaseLineViolations()
    }

    /** @private */
    _getMergedOpenTransactionStoreAsJson(): DalJsStore {
        const mergedStore = this.pagesDal._currentTransaction.store.asJson()
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            _.merge(mergedStore, fragmentDal._currentTransaction.store.asJson())
        })
        return mergedStore
    }

    /** @private */
    _getMergedTentativeStoreAsJson(): DalJsStore {
        const mergedStore = this.pagesDal._getTentativeStoreAsJson()
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            _.merge(mergedStore, fragmentDal._getTentativeStoreAsJson())
        })
        return mergedStore
    }

    /** @private */
    _getMergedApprovedStoreAsJson(): DalJsStore {
        const mergedStore = this.pagesDal._getApprovedStoreAsJson()
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            _.merge(mergedStore, fragmentDal._getApprovedStoreAsJson())
        })
        return mergedStore
    }

    /** @private */
    private get pagesDal() {
        return this.fragmentDALs[PAGE_FRAGMENT_TYPE]
    }

    /** @private */
    _getAllFragmentsOpenTransactionStoreAsJson(): FragmentDalJsStore {
        const mergedStore: FragmentDalJsStore = {}
        Object.entries(this.fragmentDALs).forEach(([fragmentType, fragmentDal]) => {
            mergedStore[fragmentType] = fragmentDal._currentTransaction.store.asJson()
        })
        return mergedStore
    }

    /** @private */
    _getAllFragmentsApprovedStoreAsJson(): FragmentDalJsStore {
        const approvedStore: FragmentDalJsStore = {}
        Object.entries(this.fragmentDALs).forEach(([fragmentType, fragmentDal]) => {
            approvedStore[fragmentType] = fragmentDal._getApprovedStoreAsJson()
        })
        return approvedStore
    }
}

export const createFragmentDalManager = ({
    coreConfig,
    postSetOperations,
    dmTypes,
    customGetters,
    eventEmitter,
    initialStore
}: CreateArgs): DAL => {
    return new FragmentDalManager(
        coreConfig,
        postSetOperations,
        dmTypes,
        customGetters,
        eventEmitter,
        initialStore as FragmentStoreMap
    )
}
